import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Measurable } from "../../types/Scenario";
import { Dash } from "../../types/Dash";
import useActions from "../../app/hooks";
import EditMeasurable from "./EditMeasurable";

const SHEET_WIDTH = 10000;
const SHEET_HEIGHT = 10000;
const MAP_SIZE = 200;
const MAP_SCALE = MAP_SIZE / SHEET_WIDTH;

/** Normalize deps from API (array, {"deps": [...]}, or JSON string) to number[] */
function getDeps(m: Measurable): number[] {
  if (m.deps == null) return [];
  if (Array.isArray(m.deps)) return m.deps;
  if (typeof m.deps === "object" && Array.isArray((m.deps as { deps: number[] }).deps)) {
    return (m.deps as { deps: number[] }).deps;
  }
  if (typeof m.deps === "string") {
    try {
      const parsed = JSON.parse(m.deps) as { deps?: number[] } | number[];
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.deps)) return parsed.deps;
    } catch {
      /* ignore */
    }
  }
  return [];
}

/** Parse for_whom string (JSON array of entities) to string[] */
function getForWhom(m: Measurable): string[] {
  const raw = m.for_whom;
  if (raw == null || raw === "") return [];
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((e): e is string => typeof e === "string");
    return [];
  } catch {
    return [];
  }
}

export default function ScenarioMeasurablesSheet(props: {
  scenarioId: number;
  measurables: Measurable[];
  loading?: boolean;
  scenarioName?: string;
  dashId?: number | null;
  /** Scenario's folder_id; used when creating a dash for a measurable. */
  scenarioFolderId?: number | null;
}) {
  const { scenarioId, measurables, loading, scenarioName, dashId, scenarioFolderId } = props;
  const actions = useActions();
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollAfterUpdateRef = useRef<{ scrollLeft: number; scrollTop: number } | null>(null);

  const [viewport, setViewport] = useState({
    scrollLeft: 0,
    scrollTop: 0,
    width: 0,
    height: 0,
  });

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setViewport({
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
      width: el.clientWidth,
      height: el.clientHeight,
    });
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, [loading]);

  // Restore scroll after async measurable update (e.g. after drag-and-drop).
  useEffect(() => {
    const pending = pendingScrollAfterUpdateRef.current;
    if (pending) {
      pendingScrollAfterUpdateRef.current = null;
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollLeft = pending.scrollLeft;
        el.scrollTop = pending.scrollTop;
      }
    }
  }, [measurables]);

  useEffect(() => {
    pendingScrollAfterUpdateRef.current = null;
  }, [scenarioId]);

  const [searchTerm, setSearchTerm] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);

  const searchMatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return measurables.filter((m) =>
      m.description.toLowerCase().includes(term)
    );
  }, [measurables, searchTerm]);

  const scrollToMeasurable = (x: number, y: number) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollLeft = Math.max(
      0,
      Math.min(x - el.clientWidth / 2, el.scrollWidth - el.clientWidth)
    );
    const scrollTop = Math.max(
      0,
      Math.min(y - el.clientHeight / 2, el.scrollHeight - el.clientHeight)
    );
    el.scrollTo({ left: scrollLeft, top: scrollTop, behavior: "smooth" });
  };

  // When opening a scenario, scroll to the first priority-one measurable (if any).
  // Wait until the scroll container ref is mounted before attempting to scroll.
  useEffect(() => {
    if (!measurables || measurables.length === 0) return;
    const firstPriorityOne = measurables.find((m) => m.priority === 1);
    if (!firstPriorityOne) return;
    const tryScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) {
        // Ref not set yet; try again on next frame.
        requestAnimationFrame(tryScroll);
        return;
      }
      scrollToMeasurable(firstPriorityOne.x, firstPriorityOne.y);
    };
    tryScroll();
  }, [scenarioId, measurables]);

  const handleMinimapDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mapX = e.clientX - rect.left;
    const mapY = e.clientY - rect.top;
    const sheetX = mapX / MAP_SCALE;
    const sheetY = mapY / MAP_SCALE;
    scrollToMeasurable(sheetX, sheetY);
  };

  const handleSearchGo = () => {
    if (searchMatches.length === 0) return;
    const idx = Math.min(matchIndex, searchMatches.length - 1);
    const m = searchMatches[idx];
    scrollToMeasurable(m.x, m.y);
    setMatchIndex(idx);
  };

  const handleSearchPrev = () => {
    if (searchMatches.length === 0) return;
    const idx = (matchIndex - 1 + searchMatches.length) % searchMatches.length;
    setMatchIndex(idx);
    const m = searchMatches[idx];
    scrollToMeasurable(m.x, m.y);
  };

  const handleSearchNext = () => {
    if (searchMatches.length === 0) return;
    const idx = (matchIndex + 1) % searchMatches.length;
    setMatchIndex(idx);
    const m = searchMatches[idx];
    scrollToMeasurable(m.x, m.y);
  };

  const [formOpen, setFormOpen] = useState(false);
  const [pendingPoint, setPendingPoint] = useState<{ x: number; y: number } | null>(null);
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [priority, setPriority] = useState<number>(0);
  const [userGuessedProbability, setUserGuessedProbability] = useState<number>(0);
  const [forWhom, setForWhom] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingMeasurable, setEditingMeasurable] = useState<Measurable | null>(null);

  const handleSheetDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = sheetRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    setPendingPoint({ x, y });
    setDescription("");
    setDescriptionError("");
    setPriority(0);
    setUserGuessedProbability(0);
    setForWhom("");
    setFormOpen(true);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDescription(v);
    setDescriptionError(v.trim() === "" ? "Description is required" : "");
  };

  const handleConfirm = () => {
    if (!pendingPoint || description.trim() === "") {
      setDescriptionError("Description is required");
      return;
    }
    const scrollEl = scrollContainerRef.current;
    if (scrollEl) {
      pendingScrollAfterUpdateRef.current = {
        scrollLeft: scrollEl.scrollLeft,
        scrollTop: scrollEl.scrollTop,
      };
    }
    const entities = forWhom
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    actions.createScenarioMeasurable({
      scenarioId,
      description,
      x: pendingPoint.x,
      y: pendingPoint.y,
      dash_id: dashId ?? null,
      priority,
      user_guessed_probability: userGuessedProbability,
      for_whom: JSON.stringify(entities),
    });
    setFormOpen(false);
    setPendingPoint(null);
    setDescription("");
    setPriority(0);
    setUserGuessedProbability(0);
    setForWhom("");
  };

  const handleClose = () => {
    setFormOpen(false);
    setPendingPoint(null);
    setDescription("");
    setDescriptionError("");
    setPriority(0);
    setUserGuessedProbability(0);
    setForWhom("");
  };

  const handleMeasurableDoubleClick = (e: React.MouseEvent, m: Measurable) => {
    e.stopPropagation();
    if (m.id == null) return;
    setEditingMeasurable(m);
    setEditOpen(true);
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setEditingMeasurable(null);
  };

  const handleEditSave = (updated: {
    description: string;
    x: number;
    y: number;
    deps: number[];
    priority: number;
    user_guessed_probability: number;
    for_whom: string;
  }) => {
    if (!editingMeasurable || editingMeasurable.id == null) return;
    actions.updateScenarioMeasurable({
      scenarioId,
      measurableId: editingMeasurable.id,
      description: updated.description,
      x: updated.x,
      y: updated.y,
      deps: updated.deps,
      dash_id: editingMeasurable.dash_id ?? dashId ?? null,
      priority: updated.priority,
      user_guessed_probability: updated.user_guessed_probability,
      for_whom: updated.for_whom,
    });
    setEditOpen(false);
    setEditingMeasurable(null);
  };

  const handleMeasurableDragStart = (e: React.DragEvent, m: Measurable) => {
    if (m.id == null) return;
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        measurableId: m.id,
        description: m.description,
        deps: getDeps(m),
        dash_id: m.dash_id ?? null,
        priority: m.priority ?? 0,
        user_guessed_probability: m.user_guessed_probability ?? 0,
        for_whom: m.for_whom ?? "[]",
      })
    );
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSheetDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleSheetDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/json");
    if (!data) return;
    let payload: {
      measurableId: number;
      description: string;
      deps?: number[];
      dash_id?: number | null;
      priority?: number;
      user_guessed_probability?: number;
      for_whom?: string | null;
    };
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    const sheetEl = sheetRef.current;
    const scrollEl = scrollContainerRef.current;
    if (!sheetEl || !scrollEl) return;
    pendingScrollAfterUpdateRef.current = {
      scrollLeft: scrollEl.scrollLeft,
      scrollTop: scrollEl.scrollTop,
    };
    // Sheet rect is in viewport coords; when scrolled, rect.left/top already reflect scroll,
    // so (e.clientX - rect.left) is the sheet x coordinate. Do not add scrollLeft/scrollTop.
    const rect = sheetEl.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    actions.updateScenarioMeasurable({
      scenarioId,
      measurableId: payload.measurableId,
      description: payload.description,
      x,
      y,
      deps: payload.deps ?? [],
      dash_id: payload.dash_id ?? dashId ?? null,
      priority: payload.priority ?? 0,
      user_guessed_probability: payload.user_guessed_probability ?? 0,
      for_whom: payload.for_whom ?? "[]",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search measurables..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setMatchIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearchGo();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220 }}
        />
        <Button size="small" variant="outlined" onClick={handleSearchGo} disabled={searchMatches.length === 0}>
          Go
        </Button>
        {searchTerm.trim() !== "" && searchMatches.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              {matchIndex + 1} of {searchMatches.length}
            </Typography>
            <IconButton size="small" onClick={handleSearchPrev} title="Previous match" disabled={searchMatches.length <= 1}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton size="small" onClick={handleSearchNext} title="Next match" disabled={searchMatches.length <= 1}>
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </Box>
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          overflow: "auto",
          maxHeight: "calc(100vh - 160px)",
          position: "relative",
        }}
      >
        <Paper
          ref={sheetRef}
          elevation={2}
          onDoubleClick={handleSheetDoubleClick}
          onDragOver={handleSheetDragOver}
          onDrop={handleSheetDrop}
          sx={{
            position: "relative",
            width: SHEET_WIDTH,
            minHeight: SHEET_HEIGHT,
            height: SHEET_HEIGHT,
            bgcolor: "background.paper",
            cursor: "crosshair",
          }}
        >
          {/* Dependency edges (arrows from dependency -> dependent); arrow at midpoint */}
          <svg
            width={SHEET_WIDTH}
            height={SHEET_HEIGHT}
            style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
          >
            {measurables.map((m) => {
              const deps = getDeps(m);
              if (deps.length === 0 || m.id == null) return null;
              return deps.map((depId) => {
                const source = measurables.find((o) => o.id === depId);
                if (!source) return null;
                const dx = m.x - source.x;
                const dy = m.y - source.y;
                const dist = Math.hypot(dx, dy) || 1;
                const offset = 14;
                const startX = source.x + (offset * dx) / dist;
                const startY = source.y + (offset * dy) / dist;
                const endX = m.x - (offset * dx) / dist;
                const endY = m.y - (offset * dy) / dist;
                const midX = (startX + endX) / 2;
                const midY = (startY + endY) / 2;
                const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI + 180;
                return (
                  <g key={`edge-${m.id}-${depId}`}>
                    <line
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      stroke="#333"
                      strokeWidth={2}
                    />
                    <polygon
                      points="0,0 10,-6 10,6"
                      fill="#333"
                      transform={`translate(${midX},${midY}) rotate(${angleDeg})`}
                    />
                  </g>
                );
              });
            })}
          </svg>
          {measurables.map((m, index) => (
            <Box
              key={m.id ?? index}
              draggable={m.id != null}
              onDragStart={(e) => handleMeasurableDragStart(e, m)}
              onDoubleClick={(e) => handleMeasurableDoubleClick(e, m)}
              sx={{
                position: "absolute",
                left: m.x,
                top: m.y,
                transform: "translate(-50%, -50%)",
                px: 1,
                py: 0.5,
                bgcolor: "primary.light",
                color: "#000000",
                border: m.priority === 1 ? "2px solid red" : "1px solid transparent",
                borderRadius: 1,
                maxWidth: 150,
                boxShadow: 1,
                cursor: m.id != null ? "grab" : "default",
                "&:active": m.id != null ? { cursor: "grabbing" } : {},
              }}
              title={m.id != null ? "Drag to move, double-click to edit" : m.description}
            >
              <Typography
                variant="body2"
                title={m.description}
                sx={{ whiteSpace: "normal", wordBreak: "break-word", color: "black" }}
              >
                {m.description}
              </Typography>
              {m.user_guessed_probability != null &&
                m.user_guessed_probability > 0 && (
                  <Box sx={{ mt: 0.25 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        lineHeight: 1.1,
                        color:
                          m.user_guessed_probability > 80
                            ? "error.main"
                            : "text.secondary",
                      }}
                    >
                      {m.user_guessed_probability}%
                    </Typography>
                  </Box>
                )}
              {getForWhom(m).length > 0 && (
                <Box sx={{ mt: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      lineHeight: 1.2,
                      color: "text.secondary",
                      display: "block",
                    }}
                  >
                    {getForWhom(m).join(" · ")}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      </Box>

      {/* Minimap: map of measurables in bottom right */}
      <Paper
        elevation={4}
        sx={{
          position: "absolute",
          bottom: 16,
          right: 16,
          width: MAP_SIZE,
          height: MAP_SIZE,
          bgcolor: "grey.200",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          onDoubleClick={handleMinimapDoubleClick}
          sx={{
            position: "relative",
            width: MAP_SIZE,
            height: MAP_SIZE,
            cursor: "pointer",
          }}
          title="Double-click to scroll to location"
        >
          {/* Viewport position on the map */}
          {viewport.width > 0 && viewport.height > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: viewport.scrollLeft * MAP_SCALE,
                top: viewport.scrollTop * MAP_SCALE,
                width: Math.max(viewport.width * MAP_SCALE, 4),
                height: Math.max(viewport.height * MAP_SCALE, 4),
                border: "2px solid",
                borderColor: "primary.main",
                bgcolor: "primary.main",
                opacity: 0.25,
                pointerEvents: "none",
              }}
            />
          )}
          {measurables.map((m, index) => (
            <Box
              key={m.id ?? index}
              title={m.description}
              sx={{
                position: "absolute",
                left: m.x * MAP_SCALE,
                top: m.y * MAP_SCALE,
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: "primary.main",
                transform: "translate(-50%, -50%)",
              }}
            />
          ))}
        </Box>
      </Paper>

      <Dialog open={formOpen} onClose={handleClose}>
        <DialogTitle>Add measurable</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingPoint != null &&
              `Position: (${pendingPoint.x}, ${pendingPoint.y}). Enter a description for this measurable.`}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            variant="standard"
            value={description}
            onChange={handleDescriptionChange}
            error={descriptionError !== ""}
            helperText={descriptionError}
          />
          <TextField
            margin="dense"
            label="Priority"
            type="number"
            fullWidth
            variant="standard"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            inputProps={{ step: 1, min: 0 }}
          />
          <TextField
            margin="dense"
            label="User guessed probability (0–100)"
            type="number"
            fullWidth
            variant="standard"
            value={userGuessedProbability}
            onChange={(e) => setUserGuessedProbability(Number(e.target.value))}
            inputProps={{ step: 1, min: 0, max: 100 }}
          />
          <TextField
            margin="dense"
            label="For whom (comma-separated entities)"
            fullWidth
            variant="standard"
            value={forWhom}
            onChange={(e) => setForWhom(e.target.value)}
            placeholder="e.g. Team A, Product X"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" disabled={description.trim() === ""} type="button">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <EditMeasurable
        open={editOpen}
        measurable={editingMeasurable}
        scenarioId={scenarioId}
        measurables={measurables}
        dashId={dashId}
        scenarioFolderId={scenarioFolderId}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />
    </Box>
  );
}
